import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import classNames from "classnames";
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { DropdownLink } from "./module";

type DropdownWrapperProps = {
  header?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  inactiveClasses?: string;
  activeClasses?: string;
  variant?: "contained" | "text";
  anchorOriginVertical?: number | "center" | "top" | "bottom";
  anchorOriginHorizontal?: number | "center" | "left" | "right";
  links?: DropdownLink[];
  selectorPrefix?: string;
  setIsOpen?: (open: boolean) => void;
  enforceDarkMode?: boolean;
};

type IconButtonWithDropdownLinkProps = {
  link?: DropdownLink;
};

type IconButtonWithDropdownMenuItemProps = {
  isOpen?: boolean;
  onClick?: () => void;
  onClose?: () => void;
  link: DropdownLink;
  pointerEvents?: boolean;
  selectorPrefix?: string;
  closeOnClick?: boolean;
};

const SubMenu = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    id="tooltip_menu"
    classes={{ popper: className }}
    enterDelay={0}
    enterNextDelay={0}
    leaveDelay={500}
    TransitionProps={{
      timeout: {
        enter: 200,
        exit: 100,
      },
      unmountOnExit: true,
    }}
  />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    overflow: "hidden",
    minWidth: "175px",
    minHeight: 24,
    padding: "0px",
    paddingTop: "4px",
    paddingBottom: "4px",
  },
}));

export function IconButtonWithDropdownLink({
  link,
}: IconButtonWithDropdownLinkProps) {
  const { pathname } = useLocation();
  const linkWeight = link.weight ?? "normal";

  const linkContent = (
    <div
      className={classNames(
        "flex flex-grow items-center relative",
        link.className,
      )}
    >
      {link.icon ? (
        <div className="w-7 h-7 flex items-center justify-center rounded-lg m-0 mr-0.5">
          <div
            className={`w-5 h-5 flex items-center justify-center rounded m-0 ${link.colors}`}
          >
            {link.icon}
          </div>
        </div>
      ) : null}

      <Box
        className={classNames(
          "flex items-center text-sm select-none",
          { "pr-2": !link.checkable },
          { "pr-8": link.checkable },
          { "font-normal opacity-80": linkWeight === "light" },
          { "font-[inherit]": linkWeight === "normal" },
        )}
      >
        {link.name}
      </Box>

      {link.checkable ? (
        <div className="absolute top-0 flex items-center justify-center rounded-lg -right-2 w-7 h-7">
          <div className="flex items-center justify-center w-5 h-5 m-0 rounded">
            {link.checked ? (
              <FontAwesomeIcon
                icon={solid("check")}
                className="text-xs opacity-100"
              />
            ) : (
              <FontAwesomeIcon
                icon={solid("check")}
                className="text-xs opacity-0"
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {link.route ? (
        <>
          {link.route.startsWith("http") ? (
            <a
              href={link.route}
              onClick={link.onClick}
              target="_blank"
              className={classNames({
                "menu_item mx-2": true,
                "font-bold ring-1 ring-inset text-layer-800 bg-layer-200 ring-layer-300 dark:text-layer-200 dark:bg-layer-700 dark:ring-layer-600":
                  pathname === link.route,
                "font-medium": pathname !== link.route,
                "px-2": link.icon === undefined,
              })}
              rel="noreferrer"
            >
              {linkContent}
            </a>
          ) : (
            <Link
              to={link.route}
              onClick={link.onClick}
              className={classNames({
                "menu_item mx-2": true,
                "font-bold ring-1 ring-inset text-layer-800 bg-layer-200 ring-layer-300 dark:text-layer-200 dark:bg-layer-700 dark:ring-layer-600":
                  pathname === link.route,
                "font-medium": pathname !== link.route,
                "px-2": link.icon === undefined,
              })}
            >
              {linkContent}
            </Link>
          )}
        </>
      ) : (
        <Box
          onClick={link.onClick}
          className={classNames({
            "menu_item flex flex-grow rounded-md font-medium mx-2": true,
            "hover:bg-layer-200 dark:hover:bg-layer-700/75":
              !!link.onClick && !link.disabled,
            "text-layer-800 dark:text-layer-200 cursor-not-allowed":
              !link.onClick || link.disabled,
            "px-2": link.icon === undefined,
          })}
        >
          {linkContent}
        </Box>
      )}
    </>
  );
}

export function IconButtonWithDropdownMenuItem({
  isOpen,
  onClose,
  link,
  selectorPrefix,
  onClick,
  closeOnClick = true,
  pointerEvents = true,
}: IconButtonWithDropdownMenuItemProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (closeOnClick && onClose) {
      onClose();
    }
  };

  return (
    <MenuItem
      key={link.id}
      onClick={handleClick}
      className={`group px-0 bg-transparent ${
        link.borderTop
          ? "mt-1 pt-2 border-solid border-t border-layer-200 dark:border-layer-700"
          : ""
      } ${
        link.borderBottom
          ? "mb-1 pb-2 border-solid border-b border-layer-200 dark:border-layer-700"
          : ""
      } ${pointerEvents ? "pointer-events-auto" : "pointer-events-none"}`}
      data-tour={selectorPrefix && `${selectorPrefix}-item-${link.id}`}
      data-cy={selectorPrefix && `${selectorPrefix}-item-${link.id}`}
    >
      {isOpen && link.subMenu ? (
        <SubMenu
          placement="left-start"
          title={
            <ul>
              {link.subMenu.map((subMenuLink) => {
                const closeOnClick = subMenuLink.closeOnClick ?? true;
                return (
                  <MenuItem
                    key={subMenuLink.id}
                    onClick={closeOnClick === true ? onClose : () => ({})}
                    className={`py-1 bg-transparent bg-none ${
                      subMenuLink.borderTop
                        ? "mt-2 pt-2 border-solid border-t border-layer-300"
                        : ""
                    } ${
                      subMenuLink.borderBottom
                        ? "mb-2 pb-2 border-solid border-b border-layer-300"
                        : ""
                    }`}
                  >
                    <IconButtonWithDropdownLink link={subMenuLink} />
                  </MenuItem>
                );
              })}
            </ul>
          }
        >
          <Box className="mx-2 menu_item group">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg m-0 mr-0.5 group">
              <div
                className={`w-5 h-5 flex items-center justify-center rounded m-0 ${link.colors}`}
              >
                {link.icon}
              </div>
            </div>

            <Box className="flex grow items-center text-sm font-[inherit] select-none pr-2">
              {link.name}
            </Box>

            <div className="h-7 flex items-center pt-0.5 pl-1">
              <FontAwesomeIcon
                icon={solid("caret-right")}
                className="text-xs opacity-50 group-hover:opacity-100"
              />
            </div>
          </Box>
        </SubMenu>
      ) : (
        <IconButtonWithDropdownLink link={link} />
      )}
    </MenuItem>
  );
}

export default function IconButtonWithDropdown({
  links,
  header,
  children,
  className = "flex items-center justify-center w-8 h-8 border rounded-lg pointer-events-auto",
  inactiveClasses = "",
  activeClasses = "",
  variant = "contained",
  anchorOriginVertical = "top",
  anchorOriginHorizontal = "center",
  selectorPrefix,
  setIsOpen,
}: DropdownWrapperProps) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);

  const openMenu = (event) => {
    setAnchorEl(event.target);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setIsOpen?.(!!isOpen);
  }, [setIsOpen, isOpen]);

  const buttonInactiveClasses =
    variant === "contained"
      ? inactiveClasses !== ""
        ? inactiveClasses
        : "relative shadow-button border-layer-100 bg-white text-layer-600 hover:bg-layer-100 hover:border-layer-200 hover:text-layer-700 dark:border-layer-700 dark:bg-layer-800 dark:text-layer-300 dark:hover:bg-layer-700 dark:hover:border-layer-600 dark:hover:text-layer-200"
      : "relative border-transparent text-layer-700 hover:text-layer-800 dark:text-layer-300 dark:hover:text-layer-200";
  const buttonActiveClasses =
    variant === "contained"
      ? activeClasses !== ""
        ? activeClasses
        : "border-layer-300 bg-layer-200 text-layer-900 dark:border-layer-600 dark:bg-layer-700 dark:text-layer-200"
      : "border-transparent text-layer-900 dark:text-layer-50";

  return (
    <>
      <Box
        onClick={(event) => openMenu(event)}
        className={`${className} group ${
          isOpen ? buttonActiveClasses : buttonInactiveClasses
        }`}
        data-tour={selectorPrefix && `${selectorPrefix}-button`}
        data-cy={selectorPrefix && `${selectorPrefix}-button`}
      >
        <IconButton
          className="w-8 h-8 flex items-center justify-center bg-transparent m-0 p-0 text=[inherit]"
          size="large"
          edge="end"
          aria-haspopup="true"
          color="inherit"
        >
          {children}
        </IconButton>
      </Box>

      <Menu
        open={isOpen}
        onClose={closeMenu}
        anchorEl={anchorEl}
        TransitionProps={{
          timeout: {
            enter: 200,
            exit: 100,
          },
          unmountOnExit: true,
        }}
        anchorOrigin={{
          vertical: anchorOriginVertical,
          horizontal: anchorOriginHorizontal,
        }}
        keepMounted
        transformOrigin={{
          vertical: anchorOriginVertical,
          horizontal: anchorOriginHorizontal,
        }}
        sx={{
          "& .MuiList-padding": {
            paddingTop: "4px !important",
            paddingBottom: "4px !important",
          },

          "& .MuiPaper-root": {
            marginTop:
              (anchorOriginVertical === "top" && "32px") ||
              (anchorOriginVertical === "bottom" && "-32px"),
            minWidth: "175px",
          },
        }}
      >
        {header}

        {links.filter(Boolean).map((link) => {
          return (
            <IconButtonWithDropdownMenuItem
              key={link.id}
              closeOnClick={link.closeOnClick}
              pointerEvents={link.pointerEvents ?? true}
              link={link}
              isOpen={isOpen}
              onClose={closeMenu}
              selectorPrefix={selectorPrefix}
            />
          );
        })}
      </Menu>
    </>
  );
}
